import { activity_recent, and, desc, eq, type PowerSyncDrizzleDB } from "@journal/powersync";

const DEFAULT_FEED_LIMIT = 200;

/**
 * PLUGIN-KERNEL: the app-specific dimensions the feed can be narrowed by.
 *
 * journal's feed has none — the local SQLite already contains only this
 * account's personal journal entries. Every dimension is `never`, so the
 * only filter that type-checks is the empty one.
 */
export type ActivityFeedFilter = Record<string, never>;

export interface ActivityFeedOptions {
  limit?: number;
}

/**
 * Local activity feed (PowerSync `activity_recent` bucket, ~90 days).
 * The account's SQLite only contains its personal scope (the journal is private),
 * so there is no need to re-filter by user.
 * Canonical order: server_ts DESC (tie-break by UUIDv7 id).
 */
export function getActivityFeedQuery(db: PowerSyncDrizzleDB, options: ActivityFeedOptions = {}) {
  return db
    .select()
    .from(activity_recent)
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}

/**
 * Local history for a specific entity (for the entry detail's "History" sheet).
 * Deep history (beyond retention) is requested from the API
 * (`GET /api/activity/entity/:type/:id`).
 */
export function getEntityActivityQuery(
  db: PowerSyncDrizzleDB,
  entityType: string,
  entityId: string,
  options: { limit?: number } = {},
) {
  return db
    .select()
    .from(activity_recent)
    .where(
      and(eq(activity_recent.entity_type, entityType), eq(activity_recent.entity_id, entityId)),
    )
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}
